import '@/styles/globals.css'
import type { AppProps } from 'next/app'
import Layout from '@/components/Layout'
import { ThemeProvider } from '@/contexts/ThemeContext'
import { useEffect } from 'react'
import { useRouter } from 'next/router'
import { performance, resourceHints } from '@/lib/performance'
import { registerServiceWorker, setupConnectionHandlers } from '@/lib/serviceWorker'
import { initializePerformanceOptimizations, optimizeFontLoading } from '@/lib/performanceOptimizer'
import { initializeComponentLoading } from '@/lib/componentLoader'
import { gtag, performanceMonitoring, initializeAnalytics, consentManager } from '@/lib/analytics'

export default function App({ Component, pageProps }: AppProps) {
  const router = useRouter()

  // Track page views
  useEffect(() => {
    const handleRouteChange = (url: string) => {
      gtag.pageview(url)
    }

    router.events.on('routeChangeComplete', handleRouteChange)
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange)
    }
  }, [router.events])

  useEffect(() => {
    // Performance monitoring
    performance.mark('app-start')

    // Initialize analytics and consent management
    consentManager.initConsent()
    initializeAnalytics()

    // Initialize all performance optimizations
    initializePerformanceOptimizations()

    // Initialize component loading optimizations
    initializeComponentLoading()

    // Optimize font loading
    optimizeFontLoading()

    // Preconnect to external domains (already handled in performanceOptimizer)
    resourceHints.preconnect('https://cdn.sanity.io')
    resourceHints.preconnect('https://fonts.googleapis.com')
    resourceHints.preconnect('https://fonts.gstatic.com', true)

    // Register service worker for caching
    registerServiceWorker()

    // Setup connection status handlers
    setupConnectionHandlers()

    // Report Core Web Vitals (in production)
    if (process.env.NODE_ENV === 'production') {
      const reportWebVitals = async () => {
        try {
          const [cls, lcp, fid]: number[] = (await Promise.all([
            performance.getCLS(),
            performance.getLCP(),
            performance.getFID(),
          ])) as number[]

          // Send to analytics using our analytics utility
          performanceMonitoring.trackWebVitals({ name: 'CLS', value: cls * 1000, id: 'cls' })
          performanceMonitoring.trackWebVitals({ name: 'LCP', value: lcp, id: 'lcp' })
          performanceMonitoring.trackWebVitals({ name: 'FID', value: fid, id: 'fid' })

          // Log metrics for debugging
          console.log('Core Web Vitals:', { cls, lcp, fid })
        } catch (error) {
          console.warn('Failed to collect Core Web Vitals:', error)
        }
      }

      // Delay reporting to allow page to fully load
      setTimeout(reportWebVitals, 2000)
    }

    // Development-only performance monitoring
    if (process.env.NODE_ENV === 'development') {
      // Monitor memory usage
      const monitorMemory = () => {
        if ('memory' in performance) {
          const memory = (performance as any).memory
          console.log('Memory Usage:', {
            used: `${(memory.usedJSHeapSize / 1048576).toFixed(2)} MB`,
            total: `${(memory.totalJSHeapSize / 1048576).toFixed(2)} MB`,
            limit: `${(memory.jsHeapSizeLimit / 1048576).toFixed(2)} MB`,
          })
        }
      }

      // Monitor every 30 seconds in development
      const memoryInterval = setInterval(monitorMemory, 30000)

      // Cleanup interval
      return () => {
        clearInterval(memoryInterval)
        if (typeof window !== 'undefined' && window.performance) {
          try {
            window.performance.clearMarks('app-start')
          } catch (error) {
            // Ignore cleanup errors
          }
        }
      }
    }

    // Cleanup performance marks
    return () => {
      if (typeof window !== 'undefined' && window.performance) {
        try {
          window.performance.clearMarks('app-start')
        } catch (error) {
          // Ignore cleanup errors
        }
      }
    }
  }, [])

  // Check if the page has a getLayout function
  const getLayout =
    (Component as any).getLayout || ((page: React.ReactElement) => <Layout>{page}</Layout>)

  return <ThemeProvider>{getLayout(<Component {...pageProps} />)}</ThemeProvider>
}
